/**
 * Home-screen icon drawn on the device. The manifest and apple-touch-icon are
 * swapped for data URLs at runtime, so no server assets and it works offline.
 * An installed app keeps the icon it was installed with: reinstall to change it.
 */
import { NOW_COLOUR, tokens, type Hue } from './colour'

// the Horizon marks first: the day as a line with now lit on it, a sunrise, the sun's arc
export const ICON_STYLES = [
  'horizon',
  'sunrise',
  'arc',
  'ring',
  'dot',
  'check',
  'bolt',
  'letter',
  'os',
] as const
export type IconStyle = (typeof ICON_STYLES)[number]
export const ICON_BGS = ['sky', 'night', 'black', 'ink', 'accent', 'paper'] as const
export type IconBg = (typeof ICON_BGS)[number]
export interface AppIcon {
  style: IconStyle
  colour: string
  bg: IconBg
}
export const DEFAULT_APP_ICON: AppIcon = { style: 'horizon', colour: 'slate', bg: 'sky' }

const roundRect = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) => {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}

/** Render one icon as a PNG data URL. `rounded` false gives a full-bleed square for maskable icons. */
export const renderAppIcon = (opts: AppIcon, size = 512, rounded = true, letter = 'A'): string => {
  const c = document.createElement('canvas')
  c.width = size
  c.height = size
  const ctx = c.getContext('2d')
  if (!ctx) return ''
  const t = tokens(opts.colour as Hue)
  const dark = opts.bg === 'sky' || opts.bg === 'night' || opts.bg === 'black'
  // the Horizon backgrounds: a dusk sky (indigo into violet into ember at the horizon), the night, true black
  let bg: string | CanvasGradient =
    opts.bg === 'ink'
      ? '#0a0f1a'
      : opts.bg === 'paper'
        ? '#f6f5f1'
        : opts.bg === 'night'
          ? '#15142b'
          : opts.bg === 'black'
            ? '#000000'
            : t.stripe
  if (opts.bg === 'sky') {
    const g = ctx.createLinearGradient(0, 0, 0, size)
    g.addColorStop(0, '#1c1b4d')
    g.addColorStop(0.55, '#4a2f6e')
    g.addColorStop(0.82, '#9a5a5c')
    g.addColorStop(1, '#d08a45')
    bg = g
  }
  // on the dark backgrounds the mark is the hue's pale tint, so it reads on the sky
  const fg = opts.bg === 'accent' ? '#0b1220' : dark ? t.text : t.stripe
  if (rounded) roundRect(ctx, 0, 0, size, size, size * 0.22)
  else ctx.rect(0, 0, size, size)
  ctx.fillStyle = bg
  ctx.fill()
  const cx = size / 2
  const cy = size / 2
  ctx.fillStyle = fg
  ctx.strokeStyle = fg
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  // now: the one warm point, with a soft halo
  const now = (x: number, y: number, r: number) => {
    ctx.save()
    ctx.fillStyle = NOW_COLOUR
    ctx.globalAlpha = 0.28
    ctx.beginPath()
    ctx.arc(x, y, r * 1.9, 0, Math.PI * 2)
    ctx.fill()
    ctx.globalAlpha = 1
    ctx.beginPath()
    ctx.arc(x, y, r, 0, Math.PI * 2)
    ctx.fill()
    ctx.restore()
  }
  switch (opts.style) {
    case 'horizon': {
      // the day: a line from waking to lights out, the part already gone drawn full, now lit on it
      const y = size * 0.6
      const x0 = size * 0.17
      const x1 = size * 0.83
      const at = size * 0.62
      ctx.lineWidth = size * 0.05
      ctx.globalAlpha = 0.35
      ctx.beginPath()
      ctx.moveTo(x0, y)
      ctx.lineTo(x1, y)
      ctx.stroke()
      ctx.globalAlpha = 1
      ctx.beginPath()
      ctx.moveTo(x0, y)
      ctx.lineTo(at, y)
      ctx.stroke()
      now(at, y, size * 0.075)
      break
    }
    case 'sunrise': {
      const y = size * 0.64
      ctx.save()
      ctx.fillStyle = NOW_COLOUR
      ctx.beginPath()
      ctx.arc(cx, y, size * 0.2, Math.PI, 0)
      ctx.closePath()
      ctx.fill()
      ctx.restore()
      ctx.lineWidth = size * 0.05
      ctx.beginPath()
      ctx.moveTo(size * 0.16, y)
      ctx.lineTo(size * 0.84, y)
      ctx.stroke()
      break
    }
    case 'arc': {
      // the sun's path over the day, with now partway along it
      const y = size * 0.68
      const r = size * 0.3
      ctx.lineWidth = size * 0.045
      ctx.globalAlpha = 0.4
      ctx.beginPath()
      ctx.arc(cx, y, r, Math.PI, 0)
      ctx.stroke()
      ctx.globalAlpha = 1
      const a = Math.PI + Math.PI * 0.62
      ctx.beginPath()
      ctx.arc(cx, y, r, Math.PI, a)
      ctx.stroke()
      ctx.beginPath()
      ctx.moveTo(size * 0.14, y)
      ctx.lineTo(size * 0.86, y)
      ctx.stroke()
      now(cx + r * Math.cos(a), y + r * Math.sin(a), size * 0.065)
      break
    }
    case 'dot':
      ctx.beginPath()
      ctx.arc(cx, cy, size * 0.24, 0, Math.PI * 2)
      ctx.fill()
      break
    case 'ring':
      ctx.lineWidth = size * 0.07
      ctx.beginPath()
      ctx.arc(cx, cy, size * 0.26, 0, Math.PI * 2)
      ctx.stroke()
      ctx.beginPath()
      ctx.arc(cx, cy, size * 0.12, 0, Math.PI * 2)
      ctx.fill()
      break
    case 'check':
      ctx.lineWidth = size * 0.09
      ctx.beginPath()
      ctx.moveTo(size * 0.3, size * 0.52)
      ctx.lineTo(size * 0.45, size * 0.67)
      ctx.lineTo(size * 0.72, size * 0.36)
      ctx.stroke()
      break
    case 'bolt':
      ctx.beginPath()
      ctx.moveTo(size * 0.56, size * 0.2)
      ctx.lineTo(size * 0.34, size * 0.54)
      ctx.lineTo(size * 0.5, size * 0.54)
      ctx.lineTo(size * 0.44, size * 0.8)
      ctx.lineTo(size * 0.68, size * 0.44)
      ctx.lineTo(size * 0.52, size * 0.44)
      ctx.closePath()
      ctx.fill()
      break
    case 'letter':
      ctx.font = `700 ${size * 0.56}px "Atkinson Hyperlegible Next Variable", -apple-system, "SF Pro Display", system-ui, sans-serif`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(letter, cx, cy + size * 0.03)
      break
    case 'os':
      ctx.font = `800 ${size * 0.4}px "Atkinson Hyperlegible Next Variable", -apple-system, "SF Pro Display", system-ui, sans-serif`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.letterSpacing = `${-size * 0.02}px`
      ctx.fillText('OS', cx, cy + size * 0.03)
      break
  }
  return c.toDataURL('image/png')
}

let manifestBase: Record<string, unknown> | null = null
let blobUrl: string | null = null

/** Point the manifest and apple-touch-icon at freshly drawn icons. */
export const applyAppIcon = async (opts: AppIcon, letter = 'A') => {
  try {
    const link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]')
    if (!manifestBase && link?.href) {
      const res = await fetch(link.href.startsWith('blob:') ? '/manifest.webmanifest' : link.href)
      manifestBase = (await res.json()) as Record<string, unknown>
    }
    const touch = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]')
    if (touch) touch.href = renderAppIcon(opts, 180, true, letter)
    const fav = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
    if (fav) {
      fav.href = renderAppIcon(opts, 64, true, letter)
      fav.type = 'image/png'
    }
    if (!link || !manifestBase) return
    const manifest = {
      ...manifestBase,
      icons: [
        { src: renderAppIcon(opts, 192, true, letter), sizes: '192x192', type: 'image/png' },
        { src: renderAppIcon(opts, 512, true, letter), sizes: '512x512', type: 'image/png' },
        {
          src: renderAppIcon(opts, 512, false, letter),
          sizes: '512x512',
          type: 'image/png',
          purpose: 'maskable',
        },
      ],
    }
    if (blobUrl) URL.revokeObjectURL(blobUrl)
    blobUrl = URL.createObjectURL(new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' }))
    link.href = blobUrl
  } catch {
    /* previews and private windows may block canvas or fetch; the static icon stays */
  }
}
